import type { AgentResearchEntitlement, AgentResearchJob, PrivateAgentProfile } from "@salesko/contracts";
import { Button } from "@salesko/design-system";
import { Search } from "lucide-react";
import type { CSSProperties } from "react";
import { useI18n, type Dictionary } from "../../i18n";
import { AgentResearchApiError } from "../../lib/api";
import { agentResearchJobIsTerminal, PrivateAgentResearchPreconditionError } from "../hooks/usePrivateAgentJobs";
import type { PrivateAgentComputerAvailability } from "./PrivateAgentJobsPanel";
import { privateAgentSelectionCopy } from "./PrivateAgentSelector";

/**
 * Why the trigger cannot start research right now. Every value has copy and a
 * way out; the button is never silently disabled.
 */
export type PrivateAgentResearchBlockReason =
  | "loading"
  | "signed_out"
  | "no_entitlement"
  | "no_profile"
  | "no_active_computer"
  | "active_job"
  | "no_committed_frame"
  | null;

const sectionStyle: CSSProperties = { display: "grid", gap: "8px" };
const reasonStyle: CSSProperties = {
  margin: 0,
  color: "var(--text-muted)",
  fontFamily: "var(--font-sans)",
  fontSize: "var(--text-caption)"
};

/**
 * The single admission read shared by the button and its explanation.
 *
 * An offline computer is deliberately not a blocker: the API queues the job and
 * the paired daemon claims it when it next checks in. Only the absence of an
 * *active* computer is, because that is what the API's admission policy turns
 * into a terminal `waiting_device`.
 */
export function privateAgentResearchBlockReason(input: {
  computerAvailability: PrivateAgentComputerAvailability;
  entitlement: AgentResearchEntitlement | null;
  frameRecordId: string | null;
  hasSession: boolean;
  jobs: ReadonlyArray<Pick<AgentResearchJob, "status">>;
  profile: PrivateAgentProfile | null;
}): PrivateAgentResearchBlockReason {
  if (!input.hasSession) {
    return "signed_out";
  }
  if (!input.entitlement) {
    return "loading";
  }
  if (!input.entitlement.eligible) {
    return "no_entitlement";
  }
  if (!input.profile) {
    return "no_profile";
  }
  if (input.computerAvailability === "missing" || input.computerAvailability === "revoked") {
    return "no_active_computer";
  }
  if (input.jobs.some((job) => !agentResearchJobIsTerminal(job.status))) {
    return "active_job";
  }
  if (!input.frameRecordId) {
    return "no_committed_frame";
  }
  return null;
}

/** Create-path error codes that are product states, mapped to their own copy. */
export function researchCreateErrorMessage(error: unknown, t: Dictionary): string {
  if (error instanceof PrivateAgentResearchPreconditionError) {
    return t.privateAgentJobs.trigger.frameVersionUnavailable;
  }
  if (!(error instanceof AgentResearchApiError)) {
    return t.privateAgentJobs.trigger.createError;
  }
  switch (error.code) {
    case "agent_research_frame_version_conflict":
      return t.privateAgentJobs.frameConflict;
    case "agent_research_active_job_exists":
      return t.privateAgentJobs.trigger.activeJobConflict;
    case "private_agent_profile_required":
      return t.privateAgentJobs.trigger.profileRequired;
    case "agent_research_personal_sidecar_required":
      return t.privateAgentJobs.trigger.entitlementRequired;
    case "agent_research_insufficient_credits":
      return t.privateAgentJobs.trigger.insufficientCredits;
    default:
      return t.privateAgentJobs.trigger.createError;
  }
}

export type PrivateAgentResearchTriggerProps = {
  agentSelected: boolean;
  agentName: string | null;
  blockReason: PrivateAgentResearchBlockReason;
  createError: unknown;
  createPending: boolean;
  onOpenComputers: () => void;
  onOpenJobs: () => void;
  onOpenPlans: () => void;
  onOpenPrivateAgentSettings: () => void;
  onStart: () => void;
};

/**
 * "Research with <agent>" on a person or opportunity. Presentation only; the
 * host owns the create call and the entitlement/device reads behind
 * `blockReason`.
 */
export function PrivateAgentResearchTrigger({
  agentSelected,
  agentName,
  blockReason,
  createError,
  createPending,
  onOpenComputers,
  onOpenJobs,
  onOpenPlans,
  onOpenPrivateAgentSettings,
  onStart
}: PrivateAgentResearchTriggerProps) {
  const { t, locale } = useI18n();
  const label = agentName ? t.privateAgentJobs.trigger.action(agentName) : t.privateAgentJobs.trigger.actionFallback;

  return (
    <section className="detail-section" aria-label={t.privateAgentJobs.trigger.ariaLabel} style={sectionStyle}>
      <p className="eyebrow">{t.privateAgentJobs.trigger.eyebrow}</p>
      <div>
        <Button
          disabled={!agentSelected || blockReason !== null || createPending}
          iconLeft={<Search size={15} />}
          onClick={onStart}
          size="sm"
        >
          {createPending ? t.privateAgentJobs.trigger.starting : label}
        </Button>
      </div>
      {!agentSelected ? <p style={reasonStyle}>{privateAgentSelectionCopy[locale].required}</p> : blockReason ? (
        <>
          <p style={reasonStyle}>{blockReasonCopy(blockReason, t)}</p>
          {blockReasonAction(blockReason, {
            onOpenComputers,
            onOpenJobs,
            onOpenPlans,
            onOpenPrivateAgentSettings,
            t
          })}
        </>
      ) : null}
      {createError ? (
        <p role="alert" style={reasonStyle}>
          {researchCreateErrorMessage(createError, t)}
        </p>
      ) : null}
    </section>
  );
}

function blockReasonCopy(reason: NonNullable<PrivateAgentResearchBlockReason>, t: Dictionary): string {
  switch (reason) {
    case "loading":
      return t.privateAgentJobs.trigger.checkingAccess;
    case "signed_out":
      return t.privateAgentJobs.trigger.signedOut;
    case "no_entitlement":
      return t.privateAgentJobs.trigger.entitlementRequired;
    case "no_profile":
      return t.privateAgentJobs.trigger.profileRequired;
    case "no_active_computer":
      return t.privateAgentJobs.trigger.noActiveComputer;
    case "active_job":
      return t.privateAgentJobs.trigger.activeJob;
    case "no_committed_frame":
      return t.privateAgentJobs.trigger.noCommittedFrame;
  }
}

function blockReasonAction(
  reason: NonNullable<PrivateAgentResearchBlockReason>,
  handlers: {
    onOpenComputers: () => void;
    onOpenJobs: () => void;
    onOpenPlans: () => void;
    onOpenPrivateAgentSettings: () => void;
    t: Dictionary;
  }
) {
  const { onOpenComputers, onOpenJobs, onOpenPlans, onOpenPrivateAgentSettings, t } = handlers;
  switch (reason) {
    case "no_entitlement":
      return (
        <div>
          <Button onClick={onOpenPlans} size="sm" variant="secondary">
            {t.privateAgentJobs.trigger.viewPlans}
          </Button>
        </div>
      );
    case "no_profile":
      return (
        <div>
          <Button onClick={onOpenPrivateAgentSettings} size="sm" variant="secondary">
            {t.privateAgentJobs.setUpAgent}
          </Button>
        </div>
      );
    case "no_active_computer":
      return (
        <div>
          <Button onClick={onOpenComputers} size="sm" variant="secondary">
            {t.privateAgentJobs.openComputers}
          </Button>
        </div>
      );
    case "active_job":
      return (
        <div>
          <Button onClick={onOpenJobs} size="sm" variant="secondary">
            {t.privateAgentJobs.trigger.openJobs}
          </Button>
        </div>
      );
    case "loading":
    case "signed_out":
    case "no_committed_frame":
      return null;
  }
}
